"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import AuthMethodStepView from "@/app/(discover)/components/auth/auth-method-step.view";
import { buildSignupData } from "@/components/analytics/utm-trackers";
import { OTPHeader } from "@/components/auth/auth-header";
import { formMode } from "@/components/auth/form-mode";
import GoogleSignInStepView from "@/components/auth/google-sign-in-step.view";
import LoginFormView from "@/components/auth/login-form.view";
import OTPInputContent from "@/components/auth/otp-input-content";
import useLastAuthAudience from "@/hooks/auth/use-last-auth-audience";
import useOTPRequest from "@/hooks/auth/use-otp-request";
import usePasswordlessAuth from "@/hooks/auth/use-passwordless-auth";
import { useRetryCountdown } from "@/hooks/auth/use-retry-countdown";
import useLastViewedState from "@/hooks/use-last-viewed-state";
import { useTracking } from "@/hooks/use-tracking";
import type { AuthAudience } from "@/lib/constants/auth-audiences";
import { AUTH_METHODS, type AuthMethod } from "@/lib/constants/auth-methods";
import { OTPThrottleError } from "@/lib/errors/otp-throttle-error";
import { INVALID_EMAIL_MESSAGE } from "@/lib/schemas/email";
import {
  getIndividualStateRoute,
  type IndividualStateRoute,
} from "@/lib/states-config";
import { useAuth } from "@/stores/use-auth";

const loginFormSchema = z.object({
  email: z.email(INVALID_EMAIL_MESSAGE),
  sharedDevice: z.boolean(),
});

type LoginFormData = z.infer<typeof loginFormSchema>;

interface LoginFormProps {
  mode: formMode;
  audience: AuthAudience;
  defaultEmail?: string;
  onSuccess?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

type AuthState =
  | { step: "method" }
  | { step: "google"; error?: string }
  | { step: "credentials" }
  | { step: "otp"; email: string };

function buildSignupPayload(
  email: string,
  stateRoute: IndividualStateRoute | undefined,
) {
  const { signup_action, signup_state, utmTrackers } =
    buildSignupData(stateRoute);

  return {
    email,
    agree_tos: true,
    signup_data: {
      ...utmTrackers,
      product: "connect",
      state: signup_state,
      signup_action,
    } as Record<string, unknown>,
    signup_action,
  };
}

export default function LoginFormContainer({
  mode,
  audience,
  defaultEmail,
  onSuccess,
  header,
  footer,
}: LoginFormProps) {
  const { isLoading, loadAuth } = useAuth();
  const otpRequestMutation = useOTPRequest();
  const passwordlessRegistrationMutation = usePasswordlessAuth();
  const [authState, setAuthState] = useState<AuthState>({
    step: defaultEmail ? "credentials" : "method",
  });
  const lastViewedState = useLastViewedState();
  const retryCountdown = useRetryCountdown(null);
  const { trackAuthSubmitted } = useTracking();

  const isBusy =
    isLoading ||
    otpRequestMutation.isPending ||
    passwordlessRegistrationMutation.isPending;

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: defaultEmail ?? "",
      sharedDevice: false,
    },
  });

  const { setError, clearErrors } = form;

  const handleAuthError = (error: Error) => {
    if (error instanceof OTPThrottleError) {
      retryCountdown.startCountdown(error.retryAfterSeconds);
    } else {
      retryCountdown.clearCountdown();
    }

    setError("email", {
      type: "manual",
      message: error.message,
    });
  };

  const handleOtpRequestSuccess = (email: string) => {
    setAuthState({ step: "otp", email });
  };

  const handleOtpRequest = async (email: string) => {
    if (retryCountdown.isActive) return;

    otpRequestMutation.mutate(email, {
      onSuccess: () => {
        retryCountdown.clearCountdown();
        handleOtpRequestSuccess(email);
      },
      onError: handleAuthError,
    });
  };

  const handleRegistrationSuccess = async (email: string) => {
    await handleOtpRequest(email);
  };

  const handleSignup = (email: string) => {
    const individualRoute = getIndividualStateRoute(
      lastViewedState.stateConfig?.route,
    );
    const payload = buildSignupPayload(email, individualRoute);

    passwordlessRegistrationMutation.mutate(payload, {
      onSuccess: async () => handleRegistrationSuccess(email),
      onError: handleAuthError,
    });
  };

  const handleSubmit = async (data: LoginFormData) => {
    clearErrors("email");

    if (mode === formMode.signin) {
      handleOtpRequest(data.email);
    } else if (mode === formMode.signup) {
      handleSignup(data.email);
    }
  };

  const handleGoogleError = (errorMessage: string) => {
    setAuthState((state) =>
      state.step === "google" ? { ...state, error: errorMessage } : state,
    );
  };

  const trackAuthSuccess = (authMethod: AuthMethod) => {
    trackAuthSubmitted({
      authMethod,
      sharedDevice: form.getValues("sharedDevice"),
      mode,
      audience,
    });
  };

  const handleGoogleSignInSuccess = async () => {
    useLastAuthAudience.getState().setLastAuthAudience(audience);
    trackAuthSuccess(AUTH_METHODS.GOOGLE);
    onSuccess?.();
    await loadAuth();
  };

  const handleOTPSuccess = async () => {
    useLastAuthAudience.getState().setLastAuthAudience(audience);
    trackAuthSuccess(AUTH_METHODS.OTP);
    onSuccess?.();
  };

  const handleBackToCredentials = () => {
    setAuthState({ step: "credentials" });
  };

  const handleContinueWithGoogle = () => {
    setAuthState({ step: "google" });
  };

  const handleContinueWithEmail = () => {
    setAuthState({ step: "credentials" });
  };

  const handleBackToMethod = () => {
    clearErrors("email");
    setAuthState({ step: "method" });
  };

  if (authState.step === "otp") {
    return (
      <>
        <OTPHeader email={authState.email} />
        <OTPInputContent
          email={authState.email}
          onSuccess={handleOTPSuccess}
          onBack={handleBackToCredentials}
        />
      </>
    );
  }

  if (authState.step === "method") {
    return (
      <>
        {header}
        <AuthMethodStepView
          mode={mode}
          audience={audience}
          isLoading={isBusy}
          onContinueWithGoogle={handleContinueWithGoogle}
          onContinueWithEmail={handleContinueWithEmail}
          footer={footer}
        />
      </>
    );
  }

  if (authState.step === "google") {
    return (
      <>
        {header}
        <GoogleSignInStepView
          mode={mode}
          audience={audience}
          isLoading={isBusy}
          errorMessage={authState.error}
          onBack={handleBackToMethod}
          onGoogleError={handleGoogleError}
          onGoogleSignInSuccess={handleGoogleSignInSuccess}
          control={form.control}
          footer={footer}
        />
      </>
    );
  }

  return (
    <>
      {header}
      <LoginFormView
        form={form}
        isLoading={isBusy}
        mode={mode}
        audience={audience}
        onSubmit={form.handleSubmit(handleSubmit)}
        onBack={handleBackToMethod}
        retryCountdownSeconds={retryCountdown.secondsRemaining}
        footer={footer}
      />
    </>
  );
}
